import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useAuthContext } from '~/common/contexts/auth';
import {
  InterWindowMessageType,
  type InterWindowMessage
} from '~/common/types';
import { completeConnection } from '~/common/utils/integrations-client/jira/on-prem/connection';
import { type ErrorOrNull } from '~/typings/utils';
import { useIntegrations } from '../hooks/useIntegrations';
import CommonConnected from './CommonConnected';

function JiraOnPremConnected() {
  const { t } = useTranslation();

  const [connectionsError, setConnectionsError] = useState<ErrorOrNull>(null);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [connectionDenied, setConnectionDenied] = useState(false);

  const history = useHistory();
  const {
    loading: integrationsLoading,
    error: integrationsError,
    activeEnterprise
  } = useIntegrations();

  const { user: authUser } = useAuthContext();

  useEffect(() => {
    const { oauth_token, oauth_verifier, connection_id } = queryString.parse(
      history.location.search,
      {
        // This is the default, but we're being explicit here.
        decode: true
      }
    );

    async function createConnection() {
      if (activeEnterprise && authUser) {
        try {
          setConnectionsLoading(true);

          // Use data that we got to create a new connection
          const { connection } = await completeConnection({
            enterpriseId: activeEnterprise.id,
            connectionId: connection_id as string,
            token: authUser.token,
            body: {
              oauth_token: oauth_token as string,
              oauth_verifier: oauth_verifier as string
            }
          });

          // Update main window, so it can refresh list of connections
          const messagePayload: InterWindowMessage = {
            message_type: InterWindowMessageType.REFRESH_INTEGRATION_CONNECTION,
            data: {
              connection_id: connection.id,
              product_slug: connection.product_slug
            }
          };
          window.opener.postMessage(messagePayload, location.origin);
        } catch {
          const errorMessage = t('Failed to register new connection.');
          setConnectionsError(new Error(errorMessage));
        } finally {
          setConnectionsLoading(false);
        }
      }
    }
    if (oauth_verifier === 'denied') {
      setConnectionDenied(true);
      setConnectionsLoading(false);
    } else {
      createConnection();
    }
  }, [activeEnterprise, authUser]);

  return (
    <CommonConnected
      connectionsLoading={connectionsLoading}
      integrationsLoading={integrationsLoading}
      connectionsError={connectionsError}
      integrationsError={integrationsError}
      connectionDenied={connectionDenied}
    />
  );
}

export default JiraOnPremConnected;
